
  import { Vue, Component, Prop, Watch } from "vue-property-decorator";
  import DropdownFilter from "@/components/filters/DropdownFilterNew.vue";
  import GET_PROJECTS from "@/apollo/autocomplete-queries/GetProjects.gql";
  import { Query_RootProjectArgs } from "@/generated/graphql";
  import filterMultiselectItems from "@/helpers/multiselect/filterMultiselectItems";
  import getProjectDropdownOptions from "@/helpers/multiselect/getProjectDropdownOptionsV2";
  import ProjectsMultiselectOption from "@/entities/ProjectsMultiselectOption";
  import MultiselectTag from "@/components/autocompleteMultiselects/MultiselectTagNew.vue";
  import ProjectMultiselectOption from "@/components/autocompleteMultiselects/ProjectMultiselectOptionV2.vue";

  @Component({
    components: {
      ProjectMultiselectOption,
      MultiselectTag,
      DropdownFilter,
    },
    apollo: {
      // Pre-populated projects (initial load)
      allProjects: {
        query: GET_PROJECTS,
        variables() {
          return this.initialProjectsFilter;
        },
        update(data) {
          const options = getProjectDropdownOptions(data.project);
          this.prePopulatedProjects = options;
          return options;
        },
        skip() {
          return this.hasSearchString;
        },
      },
      // Search results when user types
      searchProjects: {
        query: GET_PROJECTS,
        variables() {
          return this.projectAutocompleteFilter;
        },
        update(data) {
          const options = getProjectDropdownOptions(data.project);
          return options;
        },
        skip() {
          return !this.projectSearchString;
        },
      },
    },
  })

  export default class ProjectsMultiselect extends Vue {
    @Prop({ type: Array, required: true }) value!: string[];
    @Prop({ type: Boolean, default: false }) processing!: boolean;
    @Prop({ type: Boolean, default: false }) single!: boolean;
    @Prop({ type: Array, default: () => [] }) projectsConditions!: Object[];
    @Prop({ type: String , default: "Select Client"}) readonly placeholder?: string;
    @Prop({ type: Boolean, default: false }) readonly invalid!: boolean;
    @Prop({ type: Boolean, default: false }) readonly selectedInline!: boolean;
    @Prop({ type: Boolean, default: false }) readonly withoutLoading!: boolean;
    @Prop({ type: Number, default: undefined }) readonly max!: number;
    @Prop({ type: String, default: "" }) readonly getcontainerStyle?: string;

    projects: ProjectsMultiselectOption[] = [];
    allProjects: ProjectsMultiselectOption[] = [];
    searchProjects: ProjectsMultiselectOption[] = [];
    prePopulatedProjects: ProjectsMultiselectOption[] = [];
    chosenProjects: ProjectsMultiselectOption[] = [];
    projectSearchString = "";

    get placeholderText(): string {
      return this.placeholder ? this.placeholder : "Select client";
    }

    get hasSearchString(): boolean {
      return Boolean(this.projectSearchString && this.projectSearchString.trim().length > 0);
    }

    get initialProjectsFilter(): Query_RootProjectArgs {
      const conditions: any[] = [...this.projectsConditions];

      if (this.value.length) {
        conditions.push({
          id: {
            _nin: this.value,
          },
        });
      }

      return {
        where: {_and: conditions},
        limit: 10, // Load more for initial display
      };
    }

    get projectAutocompleteFilter(): Query_RootProjectArgs {
      const searchConditions = {
        _or: [
          {
            client_name: {
              _ilike: `%${this.projectSearchString}%`,
            },
          },
          {
            firstname: {
              _ilike: `%${this.projectSearchString}%`,
            },
          },
          {
            lastname: {
              _ilike: `%${this.projectSearchString}%`,
            },
          },
          {
            preferred_name: {
              _ilike: `%${this.projectSearchString}%`,
            },
          },
          {
            client_id: {
              _ilike: `%${this.projectSearchString}%`,
            },
          },
        ],
      };

      const conditions: any[] = [
        ...this.projectsConditions,
        searchConditions,
      ];

      if (this.value.length) {
        conditions.push({
          id: {
            _nin: this.value,
          },
        });
      }

      return {
        where: {_and: conditions},
        limit: 10,
      };
    }

    mounted() {
      this.updateProjectsList();
    }

    @Watch('allProjects')
    @Watch('searchProjects')
    @Watch('hasSearchString')
    updateProjectsList(): void {
      if (this.hasSearchString) {
        // When searching, show search results
        this.projects = this.searchProjects || [];
      } else {
        // When not searching, show pre-populated projects
        this.projects = this.allProjects || this.prePopulatedProjects || [];
      }
    }

    @Watch('value', {immediate: true})
    async onProjectsIdsChanged(): Promise<void> {
      const projectsIds = this.value;

      if (projectsIds.length === 0) {
        this.chosenProjects = [];
        return;
      }

      const {
        newItemsIds: newProjectsIds,
        previouslyChosenItems: previouslyChosenProjects,
      } = filterMultiselectItems<ProjectsMultiselectOption>(projectsIds, this.chosenProjects);

      if (newProjectsIds.length === 0) {
        return;
      }

      const rawData = await this.$apollo.query({
        query: GET_PROJECTS,
        variables: {
          where: {
            id: {
              _in: newProjectsIds,
            },
          },
        },
      })

      const newChosenProjects = getProjectDropdownOptions(rawData.data.project);

      this.chosenProjects = [...previouslyChosenProjects, ...newChosenProjects];
    }

    handleInput(chosenProjects: ProjectsMultiselectOption[]): void {
      this.chosenProjects = chosenProjects;
      const chosenProjectsIds = chosenProjects.map(option => option.id);

      this.$emit("input", chosenProjectsIds);
    }
  }
